import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import PropTypes from 'prop-types';

import CustomPagination from '../../CustomPagination';
import Loader from '../../../components/Common/Loader';
import Link from '../../Link';

import deleteUser from '../../../actions/siteadmin/deleteUser';
import getAllDrivers from './getAllDriversWithDocuments.graphql';
import messages from '../../../locale/messages';
import debounce from '../../../helpers/debounce';
import { editDriver } from '../../../actions/siteadmin/editDriver';
import { capitalizeFirstLetter } from '../../../helpers/capitalizeFirst';
import { api } from '../../../config';

import TrashIcon from '../../../../public/Icons/bin.svg';
import EditIcon from '../../../../public/Icons/edit.png';
import ExportImage from '../../../../public/Icons/export.png';
import ExportImageDark from '../../../../public/Icons/exportDark.png';
// Using edit icon as placeholder for documents
// import DocumentIcon from '../../../../public/Icons/document.svg';

import s from './DriversList.css';
import bt from '../../../components/commonStyle.css';
class DriverList extends React.Component {
  static propTypes = {
    deleteDriver: PropTypes.any
  };
  constructor(props) {
    super(props)
    this.scrollableDivRef = React.createRef();
    this.state = {
      currentPage: 1,
      searchList: '',
      typing: false,
      typingTimeout: 0
    }
  }

  getDocumentStatus = (documentExtractions) => {
    const requiredDocTypes = ['license_front', 'license_back', 'registration', 'insurance', 'vehicle_inspection', 'vehicle_plate_photo'];
    const optionalDocTypes = ['lasdri_card', 'roadworthiness', 'hackney_permit'];
    
    if (!documentExtractions || documentExtractions.length === 0) {
      return {
        requiredCompleted: 0,
        requiredTotal: 6,
        optionalCompleted: 0,
        optionalTotal: 3,
        processing: 0,
        pending: 0,
        failed: 0,
        isComplete: false
      };
    }
    
    // Group documents by type and get the latest one for each type
    const latestDocsByType = {};
    documentExtractions.forEach(doc => {
      if (!latestDocsByType[doc.documentType] || 
          new Date(doc.createdAt) > new Date(latestDocsByType[doc.documentType].createdAt)) {
        latestDocsByType[doc.documentType] = doc;
      }
    });
    
    // Count completed required documents
    const requiredCompleted = requiredDocTypes.filter(docType => 
      latestDocsByType[docType] && latestDocsByType[docType].status === 'completed'
    ).length;
    
    // Count completed optional documents
    const optionalCompleted = optionalDocTypes.filter(docType => 
      latestDocsByType[docType] && latestDocsByType[docType].status === 'completed'
    ).length;
    
    // Count status for all documents (not just latest)
    const processing = documentExtractions.filter(doc => doc.status === 'processing').length;
    const pending = documentExtractions.filter(doc => doc.status === 'pending').length;
    const failed = documentExtractions.filter(doc => doc.status === 'failed').length;
    
    return {
      requiredCompleted,
      requiredTotal: 6,
      optionalCompleted,
      optionalTotal: 3,
      processing,
      pending,
      failed,
      isComplete: requiredCompleted === 6
    };
  }

  renderDocumentStatus = (documentExtractions) => {
    const status = this.getDocumentStatus(documentExtractions);
    
    return (
      <div className={s.documentStatus}>
        <div className={cx(s.statusBadge, {
          [s.statusComplete]: status.isComplete,
          [s.statusIncomplete]: !status.isComplete && status.requiredCompleted > 0,
          [s.statusEmpty]: status.requiredCompleted === 0
        })}>
          {status.requiredCompleted}/{status.requiredTotal} Required
        </div>
        {status.optionalCompleted > 0 && (
          <div className={cx(s.statusBadge, s.statusOptional)}>
            {status.optionalCompleted}/{status.optionalTotal} Optional
          </div>
        )}
        {status.processing > 0 && (
          <div className={cx(s.statusBadge, s.statusPending)}>
            {status.processing} processing
          </div>
        )}
        {status.pending > 0 && (
          <div className={cx(s.statusBadge, s.statusPending)}>
            {status.pending} pending
          </div>
        )}
        {status.failed > 0 && (
          <div className={cx(s.statusBadge, s.statusRejected)}>
            {status.failed} failed
          </div>
        )}
      </div>
    );
  }

  handleKeywordSearch = debounce((searchList) => {
    const { driverDetails: { refetch } } = this.props;
    let variables = {
      currentPage: 1,
      searchList
    };
    this.setState(variables)
    refetch(variables);
  }, 250)

  handleDelete = async (profileId, currentPage, userType) => {
    const { deleteUser, driverDetails: { refetch, getAllDrivers } } = this.props;
    let variables = {
      currentPage: currentPage > 1 && getAllDrivers?.userData?.length == 1 ? currentPage - 1 : currentPage
    }
    const response = await deleteUser(profileId, currentPage, userType);
    if (response?.status == 200) {
      this.setState({
        currentPage: variables.currentPage
      });
      refetch(variables)
    }
  }

  handleUserStatusChange = async (e, id, email, phoneDialCode, phoneNumber, isBan, phoneCountryCode, currentPage, previousStatus) => {
    const { editDriver, driverDetails: { refetch } } = this.props;
    const newStatus = e.target.value;
    
    let data = {};
    data = {
      id: id,
      email: email,
      phoneDialCode: phoneDialCode,
      phoneNumber: phoneNumber,
      userStatus: newStatus,
      isBan: isBan,
      phoneCountryCode: phoneCountryCode
    }
    let variables = { currentPage }
    await editDriver(data);
    
    // Send approval notification if driver is being approved
    if (previousStatus !== 'active' && newStatus === 'active') {
      try {
        console.log('🎉 Driver approved! Sending notification to driver:', id);
        
        // Call the push notification endpoint
        const notificationPayload = {
          content: {
            title: "🎉 Congratulations!",
            message: "Your driver account has been approved! You can now start accepting ride requests and earn money.",
            notificationType: "driver_approved"
          },
          userId: id,
          userType: 2 // Driver type
        };
        
        const response = await fetch(`${api.apiEndpoint}/push-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(notificationPayload)
        });
        
        if (response.ok) {
          console.log('✅ Driver approval notification sent successfully');
        } else {
          console.error('❌ Failed to send driver approval notification');
        }
      } catch (error) {
        console.error('❌ Error sending driver approval notification:', error);
      }
    }
    
    refetch(variables);
  }

  handleBanStatusChange = async (e, id, email, phoneDialCode, phoneNumber, userStatus, phoneCountryCode, currentPage) => {
    const { editDriver, driverDetails: { refetch } } = this.props;
    let data = {};
    data = {
      id: id,
      email: email,
      phoneDialCode: phoneDialCode,
      phoneNumber: phoneNumber,
      userStatus: userStatus,
      isBan: e.target.value,
      phoneCountryCode: phoneCountryCode
    }
    let variables = { currentPage }
    await editDriver(data);
    refetch(variables);
  }

  paginationData = (currentPage) => {
    const { driverDetails: { refetch } } = this.props;
    let variables = { currentPage };
    this.setState({ currentPage });
    if (this.scrollableDivRef.current) {
      this.scrollableDivRef.current.scrollTop = 0;
    }
    refetch(variables);
  }

  render() {
    const { driverDetails, driverDetails: { getAllDrivers }, loading, selectedTheme } = this.props;
    const { currentPage, searchList } = this.state;
    const { formatMessage } = this.props.intl;
    let country;
    return (
      <div className={cx(s.widthInner, 'whiteDropdown', 'bgBlackTwo')}>
        <div className={bt.tableTopGrpContainer}>
          <div className={cx(s.searchInput)}>
            <FormControl type='text' placeholder={formatMessage(messages.searchOnly)} onChange={(e) => this.handleKeywordSearch(e.target && e.target.value)} className={bt.formControlInput} />
          </div>
          {
            driverDetails?.getAllDrivers?.userData && driverDetails?.getAllDrivers?.userData?.length > 0 && <a
              href={`/export-admin-data?type=drivers&keyword=${searchList ? searchList : ''}`}
              className={s.textDecorationNone}>
              <span className={cx(s.vtrMiddle, s.exportText, 'exportTextRTL')}><FormattedMessage {...messages.exportDataIntoCSV} /></span>
              <span>
                {
                  (selectedTheme && selectedTheme === "light") ?
                    <img src={ExportImage} className={cx(s.exportImg)} /> : <img src={ExportImageDark} className={cx(s.exportImg)} />
                }
              </span>
            </a>
          }
        </div>
        <div class="clearfix"></div>
        {
          loading && <div>
            <Loader type="circle" />
          </div>
        }
        {!loading &&
          <div className={cx(s.tableCss, 'tableCss', 'tableSticky', 'NewResponsiveTable', 'topScrollbarTable')} ref={this.scrollableDivRef}>
            <Table className={cx("table", 'topScrollbarTableContent')}>
              <thead>
                <tr>
                  <th scope="col"><FormattedMessage {...messages.id} /></th>
                  <th scope="col"><FormattedMessage {...messages.firstName} /></th>
                  <th scope="col"><FormattedMessage {...messages.lastName} /></th>
                  <th scope="col"><FormattedMessage {...messages.email} /></th>
                  <th scope="col"><FormattedMessage {...messages.country} /></th>
                  <th scope="col"><FormattedMessage {...messages.phoneNumber} /></th>
                  <th scope="col"><FormattedMessage {...messages.createdAt} /></th>
                  <th scope="col">Documents</th>
                  <th scope="col"><FormattedMessage {...messages.userStatus} /></th>
                  <th scope="col"><FormattedMessage {...messages.banStatus} /></th>
                  <th scope="col"><FormattedMessage {...messages.action} /></th>
                  <th scope="col"><FormattedMessage {...messages.deleteAction} /></th>
                </tr>
              </thead>
              <tbody>
                {
                  driverDetails?.getAllDrivers?.userData && driverDetails?.getAllDrivers?.userData?.length > 0 && driverDetails?.getAllDrivers?.userData?.map((item, index) => {
                    country = item && item.profile && item.profile.country;
                    return (
                      <tr key={index}>
                        <td data-label={formatMessage(messages.id)}>{item && item.profile && item.profile.profileId}</td>
                        <td data-label={formatMessage(messages.firstName)}>{item && item.profile && capitalizeFirstLetter(item.profile.firstName)}</td>
                        <td data-label={formatMessage(messages.lastName)}>{item && item.profile && capitalizeFirstLetter(item.profile.lastName)}</td>
                        <td data-label={formatMessage(messages.email)}>{item.email}</td>
                        <td data-label={formatMessage(messages.country)}>{country}</td>
                        <td data-label={formatMessage(messages.phoneNumber)}>{item && item.phoneDialCode}{item.phoneNumber}</td>
                        <td data-label={formatMessage(messages.createdAt)}>{moment(item.createdAt).format('DD-MM-YYYY HH:mm:ss')}</td>
                        <td data-label="Documents">
                          {this.renderDocumentStatus(item.documentExtractions)}
                        </td>
                        <td data-label={formatMessage(messages.userStatus)}>
                          <select name="userStatus" className={bt.selectInput}
                            onChange={(e) => this.handleUserStatusChange(e, item.id, item.email, item.phoneDialCode, item.phoneNumber, item.isBan, item.phoneCountryCode, currentPage, item.userStatus)} 
                            value={item.userStatus}>
                            <option value="pending">{formatMessage(messages.pending)}</option>
                            <option value="active">{formatMessage(messages.approve)}</option>
                            <option value="inactive">{formatMessage(messages.decline)}</option>
                          </select>
                        </td>
                        <td data-label={formatMessage(messages.banStatus)}>
                          <select name="isBan" className={bt.selectInput}
                            onChange={(e) => this.handleBanStatusChange(e, item.id, item.email, item.phoneDialCode, item.phoneNumber, item.userStatus, item.phoneCountryCode, currentPage)} value={item.isBan}>
                            <option value="0">{formatMessage(messages.permit)}</option>
                            <option value="1">{formatMessage(messages.ban)}</option>
                          </select>
                        </td>
                        <td data-label={formatMessage(messages.action)}>
                          <div className={s.actionButtons}>
                            <Link to={'/siteadmin/drivers/' + item.profile.profileId} className={cx('editAlign', s.actionBtn)}>
                              <span className={'svgImg'}><img src={EditIcon} className={cx(s.editIcon, 'editIconRTL')} /></span>
                              <span className={s.vtrMiddle}>
                                <FormattedMessage {...messages.editAction} />
                              </span>
                            </Link>
                            <Link to={'/siteadmin/drivers/' + item.id + '/documents'} className={cx('editAlign', s.actionBtn)}>
                              <span className={'svgImg'}>📄</span>
                              <span className={s.vtrMiddle}>
                                Documents
                              </span>
                            </Link>
                          </div>
                        </td>
                        <td data-label={formatMessage(messages.deleteAction)}>
                          <Button onClick={() => this.handleDelete(item.profile.profileId, currentPage, 2)} className={cx(s.iconBtn, 'svgImg', 'btnPrimaryDeleteDark', 'iconBtnRtl')}>
                            <img src={TrashIcon} className={cx(s.editIcon, 'editIconRTL')} />
                            <span className={s.vtrMiddle}>
                              <FormattedMessage {...messages.deleteAction} />
                            </span>
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                }
                {
                  ((driverDetails?.getAllDrivers?.userData?.length == 0)) && (
                    <tr>
                      <td colspan="13" className={s.noRecords}><FormattedMessage {...messages.noResult} /></td>
                    </tr>
                  )
                }
              </tbody>
            </Table>
          </div>
        }
        {
          driverDetails?.getAllDrivers?.userData && driverDetails?.getAllDrivers?.userData?.length > 0
          && <div className={cx(bt.space5, bt.spaceTop5)}>
            <CustomPagination
              total={driverDetails?.getAllDrivers?.count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={10}
              change={this.paginationData}
              paginationLabel={formatMessage(messages.drivers)}
            />
          </div>
        }
      </div>
    );
  }
}
const mapState = (state) => ({
  loading: state?.intl?.loading,
  selectedTheme: state?.currency?.theme
});
const mapDispatch = {
  deleteUser,
  editDriver
};
export default compose(injectIntl,
  withStyles(s, bt),
  graphql(getAllDrivers, {
    name: 'driverDetails',
    options: {
      variables: {
        currentPage: 1,
        searchList: ''
      },
      ssr: true,
      fetchPolicy: 'network-only'
    }
  }),
  connect(mapState, mapDispatch)
)(DriverList);
